import { motion, useInView } from "framer-motion";
import { useRef, useState } from "react";
import { Shield, Lock, Eye, UserCheck, ChevronDown } from "lucide-react";

const sections = [
  {
    title: "Data Protection",
    icon: Lock,
    colour: "#364225",
    items: [
      {
        q: "How does T.I.I.N.A. protect customer data and payment information?",
        a: "All customer data and payment data is encrypted at rest using AES-256 encryption and in transit using TLS 1.2 or higher. These are the same encryption standards used by major banks and financial institutions across New Zealand. Your information is protected at every point, whether it is being stored or being transmitted."
      },
      {
        q: "How long do you keep customer data?",
        a: "We have documented data retention processes to ensure customer data is only kept for as long as it is necessary to fulfil the purpose for which it was collected, or as required by law. When data is no longer needed, it is securely deleted in accordance with our information security policies."
      },
      {
        q: "Do you have a published privacy policy?",
        a: "Yes. T.I.I.N.A. maintains a published privacy policy that explains how we collect, use, store, and share customer data. Our policy is aligned with the New Zealand Privacy Act 2020 and is available to all customers and partners."
      },
    ],
  },
  {
    title: "Access and Authentication",
    icon: UserCheck,
    colour: "#bb321a",
    items: [
      {
        q: "Who can access customer data within T.I.I.N.A.?",
        a: "Access to customer data is restricted to authorised personnel only. We operate role-based access controls across our systems, ensuring that staff can only access the data they need to perform their specific role. Sensitive and commercially confidential data is further restricted to senior leadership."
      },
      {
        q: "Do you enforce multi-factor authentication?",
        a: "Yes. Multi-factor authentication (MFA) is enforced for all administrative access to our IT infrastructure. This ensures that even if credentials are compromised, an additional layer of verification is required before access is granted."
      },
      {
        q: "How do you manage API keys and credentials?",
        a: "All API keys and secrets provided by payment partners (including BlinkPay) are stored securely using environment-level encryption. Keys are never hardcoded, never stored in source code, and are accessible only to authorised systems and senior personnel."
      },
    ],
  },
  {
    title: "Policies and Compliance",
    icon: Shield,
    colour: "#45443f",
    items: [
      {
        q: "Does T.I.I.N.A. have documented information security policies?",
        a: "Yes. T.I.I.N.A. maintains documented information security policies and procedures that govern how we protect the confidentiality, integrity, and availability of all customer and organisational data. These policies are reviewed regularly and updated as required."
      },
      {
        q: "What security measures are in place to protect data confidentiality and integrity?",
        a: "We implement a layered security approach that includes encryption (AES-256 at rest, TLS 1.2+ in transit), role-based access controls, regular security patching, system monitoring, and secure development practices. Our infrastructure is hosted on enterprise-grade cloud platforms with built-in redundancy and security controls."
      },
      {
        q: "Do you perform regular security updates?",
        a: "Yes. We perform regular security updates and patches across all IT infrastructure. Critical patches are applied promptly, and routine updates are scheduled to ensure our systems remain secure against known vulnerabilities."
      },
      {
        q: "Do you provide security awareness training to staff?",
        a: "Yes. All T.I.I.N.A. staff receive security awareness training as part of their onboarding and ongoing professional development. This includes recognising phishing, handling sensitive data, password management, and reporting security concerns."
      },
    ],
  },
  {
    title: "Incident Response and Support",
    icon: Eye,
    colour: "#364225",
    items: [
      {
        q: "What happens if there is a security incident?",
        a: "T.I.I.N.A. has a documented incident response process. In the event of a security incident affecting customer data or payment systems, we will notify our payment partners (including BlinkPay) within 24 hours. Affected customers will be informed in accordance with our obligations under the New Zealand Privacy Act 2020."
      },
      {
        q: "How does T.I.I.N.A. handle customer complaints?",
        a: "We have a clear, documented process for handling customer complaints. Complaints are acknowledged promptly, investigated thoroughly, and resolved with transparency. Customers can raise concerns directly with our team and will receive a response within a reasonable timeframe."
      },
    ],
  },
];

export default function TrustSecuritySection() {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-60px" });
  const [open, setOpen] = useState(null);

  const toggle = (key) => setOpen(open === key ? null : key);

  return (
    <section
      id="trust"
      data-testid="trust-security-section"
      className="relative py-16 sm:py-24 overflow-hidden"
      style={{ backgroundColor: "#FDFBF7" }}
    >
      <div ref={ref} className="max-w-4xl mx-auto px-6 sm:px-8 lg:px-12 relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.6 }}
          className="mb-5"
        >
          <p className="text-sm font-bold tracking-[0.2em] uppercase text-[#364225] mb-4">Trust and Security</p>
          <h2 className="font-['Syne'] text-4xl sm:text-5xl font-bold tracking-tight text-[#bb321a] leading-[1.05]">
            Your data is <span className="text-[#364225]">protected.</span>
          </h2>
        </motion.div>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={inView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5, delay: 0.15 }}
          className="text-base sm:text-lg text-[#2D2A26] leading-relaxed max-w-3xl mb-12"
        >
          T.I.I.N.A. takes the security of your data seriously. We operate to the same standards expected by New Zealand banks and payment providers. Below is how we protect your information.
        </motion.p>

        {sections.map((section, sIdx) => {
          const Icon = section.icon;
          return (
            <motion.div
              key={section.title}
              initial={{ opacity: 0, y: 20 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.4, delay: 0.2 + sIdx * 0.08 }}
              className="mb-8"
            >
              <div className="flex items-center gap-3 mb-4">
                <div className="w-9 h-9 flex items-center justify-center" style={{ background: section.colour }}>
                  <Icon className="w-4.5 h-4.5 text-white" />
                </div>
                <h3 className="font-['Syne'] text-lg font-bold text-[#bb321a]">{section.title}</h3>
              </div>

              <div className="space-y-2">
                {section.items.map((item, iIdx) => {
                  const key = `${sIdx}-${iIdx}`;
                  const isOpen = open === key;
                  return (
                    <div
                      key={key}
                      className={`bg-white border transition-all duration-300 ${isOpen ? "border-stone-300" : "border-stone-200 hover:border-stone-300"}`}
                    >
                      <button
                        onClick={() => toggle(key)}
                        className="w-full text-left px-5 py-4 flex items-start justify-between gap-4"
                      >
                        <p className="text-sm sm:text-base font-semibold text-[#bb321a] leading-tight">{item.q}</p>
                        <ChevronDown className={`w-5 h-5 text-stone-400 flex-shrink-0 mt-0.5 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                      </button>
                      <div className={`transition-all duration-400 overflow-hidden ${isOpen ? "max-h-[500px] opacity-100" : "max-h-0 opacity-0"}`}>
                        <div className="px-5 pb-5">
                          <p className="text-sm text-[#44403C] leading-relaxed">{item.a}</p>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
}
